"use client";

import { useState } from "react";

export interface BadgeItem {
  key: string | number;
  slug: string;
  name: string;
  icon?: string | null;
  dimension?: string | null;
}

const DIMENSION_STYLES: Record<string, string> = {
  activity: "bg-blue-500/20 text-blue-300",
  industry: "bg-green-500/20 text-green-300",
  tech: "bg-purple-500/20 text-purple-300",
  model: "bg-amber-500/20 text-amber-300",
};

const FALLBACK_STYLE = "bg-gray-500/20 text-gray-300";

interface CategoryBadgePillsProps {
  items: BadgeItem[];
  max?: number;
  className?: string;
  onSelect?: (item: BadgeItem) => void;
  selectedSlugs?: Set<string>;
}

export default function CategoryBadgePills({
  items,
  max,
  className = "mb-4",
  onSelect,
  selectedSlugs,
}: CategoryBadgePillsProps) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const shown = max && !expanded ? items.slice(0, max) : items;
  const rest = items.length - shown.length;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {shown.map((item) => {
        const style = item.dimension ? DIMENSION_STYLES[item.dimension] ?? FALLBACK_STYLE : FALLBACK_STYLE;
        const active = selectedSlugs?.has(item.slug) ?? false;
        const content = (
          <>
            {item.icon && <span>{item.icon}</span>}
            <span>{item.name}</span>
          </>
        );

        if (!onSelect) {
          return (
            <span
              key={item.key}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${style}`}
            >
              {content}
            </span>
          );
        }

        return (
          <button
            key={item.key}
            type="button"
            title={item.name}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSelect(item);
            }}
            className={`relative z-10 inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-all hover:brightness-125 ${
              active ? "ring-2 ring-white/70" : ""
            } ${style}`}
          >
            {content}
          </button>
        );
      })}
      {rest > 0 && (
        <button
          type="button"
          title={`+${rest}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setExpanded(true);
          }}
          className="relative z-10 inline-flex cursor-pointer items-center rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-white/60 transition-colors hover:bg-white/20 hover:text-white/85"
        >
          +{rest}
        </button>
      )}
    </div>
  );
}
